import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import SearchHeader from './SearchHeader';
import FilterPanel from './FilterPanel';
import SearchResults from './SearchResults';
import LoadingSpinner from '../common/LoadingSpinner';
import { getCookie } from '../utils/utils';
import {
  SearchFilters,
  SearchResult,
  SearchPagination,
  LocationFilter
} from './types';
import './ExploreListings.scss';
import { CloseIcon } from '../common/CloseIcon';

interface ExploreListingsProps {
  onClose: () => void;
  isLoggedIn: boolean;
  setLoginModalVisible: (visible: boolean) => void;
  onSelectBuilding: (buildingId: string) => void;
  setFocusToLocationInput?: boolean;
  showFilters?: boolean;
  setShowFilters?: (showFilters: boolean) => void;
  countryOfFeedContent: string;
  setShowRemoveListingDialog: (listingId: string | null) => void;
  showRemoveListingDialog: string | null;
  fetchFeed: (loadMore?: boolean) => void;
  handleRefreshFeedClick: () => void;
}

const ExploreListings: React.FC<ExploreListingsProps> = ({
  onClose,
  isLoggedIn,
  setLoginModalVisible,
  onSelectBuilding,
  setFocusToLocationInput = false,
  showFilters,
  setShowFilters,
  countryOfFeedContent,
  setShowRemoveListingDialog,
  showRemoveListingDialog,
  fetchFeed,
  handleRefreshFeedClick
}) => {
  const { t } = useTranslation();
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({
    listingType: 'both',
    location: null,
    priceRange: { min: null, max: null },
    size: { min: null, max: null },
    rooms: null,
    floor: { min: null, max: null },
    renovationStatus: null,
    hasElevator: null,
    hasBalcony: null
  });

  const [noFiltersSet, setNoFiltersSet] = useState(true);
  const [showHeaders, setShowHeaders] = useState(true);
  const [lastScrollTop, setLastScrollTop] = useState(0);

  const [results, setResults] = useState<SearchResult[]>([]);
  const [secondaryResults, setSecondaryResults] = useState<SearchResult[]>([]);
  const [hasDetailFilters, setHasDetailFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [pagination, setPagination] = useState<SearchPagination>({
    offset: 0,
    limit: 20,
    total: 0,
    has_more: true
  });
  const [secondaryPagination, setSecondaryPagination] = useState<SearchPagination>({
    offset: 0,
    limit: 20,
    total: 0,
    has_more: false
  });

  // Perform search whenever filters change
  useEffect(() => {
    performSearch(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilters]);

  // Handle window resize to show headers on desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setShowHeaders(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (searchFilters.location === null
      && searchFilters.listingType === 'both'
      && searchFilters.priceRange.min === null
      && searchFilters.priceRange.max === null
      && searchFilters.size.min === null
      && searchFilters.size.max === null
      && searchFilters.rooms === null
      && searchFilters.floor.min === null
      && searchFilters.floor.max === null
      && searchFilters.renovationStatus === null
      && searchFilters.hasElevator === null
      && searchFilters.hasBalcony === null) {
      setNoFiltersSet(true);
    } else {
      setNoFiltersSet(false);
    }
  }, [searchFilters]);

  const performSearch = useCallback(async (loadMore: boolean = false) => {
    if (!loadMore) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const offset = loadMore ? pagination.offset + pagination.limit : 0;
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/search-off-market-listings`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': getCookie('csrf_access_token') || '',
          },
          body: JSON.stringify({
            country: countryOfFeedContent,
            filters: searchFilters,
            offset,
            limit: pagination.limit
          })
        }
      );

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();

      // Update hasDetailFilters flag
      setHasDetailFilters(data.has_detail_filters || false);

      if (loadMore) {
        // Check if we need to handle secondary results
        if (data.secondary_results) {
          // We're loading from secondary list
          setSecondaryResults(prev => [...prev, ...data.secondary_results]);
          setSecondaryPagination({
            offset: data.secondary_pagination.offset,
            limit: data.secondary_pagination.limit,
            total: data.secondary_total,
            has_more: data.secondary_pagination.has_more
          });
        } else {
          // Still loading from primary list
          setResults(prev => [...prev, ...data.results]);
        }
      } else {
        // Fresh search
        setResults(data.results);
        
        // Check if backend sent secondary results
        if (data.secondary_results) {
          setSecondaryResults(data.secondary_results);
          setSecondaryPagination({
            offset: data.secondary_pagination.offset,
            limit: data.secondary_pagination.limit,
            total: data.secondary_total,
            has_more: data.secondary_pagination.has_more
          });
        } else {
          // No secondary results from backend
          setSecondaryResults([]);
          setSecondaryPagination({
            offset: 0,
            limit: 20,
            total: 0,
            has_more: false
          });
        }
      }

      setPagination({
        offset: data.pagination.offset,
        limit: data.pagination.limit,
        total: data.total,
        has_more: data.pagination.has_more
      });
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [searchFilters, pagination.offset, pagination.limit, countryOfFeedContent]);

  const handleLocationChange = useCallback((location: LocationFilter | null) => {
    setSearchFilters(prev => ({ ...prev, location }));
  }, []);

  const handleFilterChange = useCallback((newFilters: Partial<SearchFilters>) => {
    setSearchFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const handleLoadMore = useCallback(() => {
    if (!isLoadingMore && pagination.has_more) {
      performSearch(true);
    }
  }, [isLoadingMore, pagination.has_more, performSearch]);

  const handleContactUser = useCallback(async (result: SearchResult) => {
    if (!isLoggedIn) {
      setLoginModalVisible(true);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/conversations`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': getCookie('csrf_access_token') || '',
          },
          body: JSON.stringify({
            buildingId: result.building_id,
            messageType: 'SEARCH_CONTACT',
            initialMessage: result.could_sell
              ? t('exploreListings.interestedInBuying')
              : t('exploreListings.interestedInSelling')
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to create conversation');
      }

      // Navigate to messages modal or show success
      // This will be handled by parent component
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
  }, [isLoggedIn, setLoginModalVisible, t]);

  const handleViewOnMap = useCallback((result: SearchResult) => {
    onSelectBuilding(result.building_id);
    // onClose();
  }, [onSelectBuilding]);

  const handleRemoveResult = useCallback((listingId: string) => {
    // Remove from both primary and secondary results
    setResults(prev => prev.filter(result => result._id !== listingId));
    setSecondaryResults(prev => prev.filter(result => result._id !== listingId));
    
    // Update total counts
    setPagination(prev => ({
      ...prev,
      total: Math.max(0, prev.total - 1)
    }));
    setSecondaryPagination(prev => ({
      ...prev,
      total: Math.max(0, prev.total - 1)
    }));
    fetchFeed(false);
    handleRefreshFeedClick();
  }, [fetchFeed, handleRefreshFeedClick]);

  const handleScroll = useCallback((scrollTop: number) => {
    // Only apply hide-on-scroll on mobile (< 768px)
    if (window.innerWidth >= 768) {
      if (!showHeaders) setShowHeaders(true);
      setLastScrollTop(scrollTop);
      return;
    }

    // Don't hide headers when filters are open
    if (showFilters) {
      if (!showHeaders) setShowHeaders(true);
      setLastScrollTop(scrollTop);
      return;
    }

    const toggleDelta = 15; // Minimum px of movement before toggling after initial hide/show
    const hideAfter = 10;  // Minimum scrollTop before we allow hiding

    const delta = scrollTop - lastScrollTop;

    // Ignore tiny moves
    if (Math.abs(delta) < 5) {
      return;
    }

    if (showHeaders) {
      // Decide to hide
      if (delta > toggleDelta && scrollTop > hideAfter) {
        setShowHeaders(false);
      }
    } else {
      // Decide to show (need sufficient upward scroll)
      if (delta < -toggleDelta) {
        setShowHeaders(true);
      }
    }

    setLastScrollTop(scrollTop);
  }, [lastScrollTop, showFilters, showHeaders]);

  return (
    <div className={`explore-listings ${!showHeaders ? 'explore-listings--headers-hidden' : ''}`}>
      <div className="explore-listings__headers-container">
        <div className="explore-listings__header">
          <h1 className="explore-listings__title-mobile">{t('exploreListings.title')}</h1>
          <h1 className="explore-listings__title-desktop">{t('exploreListings.titleDesktop')}</h1>
          <button className="explore-listings__close-button" onClick={onClose}>
            <CloseIcon size={20} color="#202125" />
          </button>
        </div>

        <SearchHeader
          onLocationChange={handleLocationChange}
          showFilters={showFilters}
          onToggleFilters={() => setShowFilters(!showFilters)}
          setFocusToLocationInput={setFocusToLocationInput}
        />
      </div>

      <FilterPanel
        filters={searchFilters}
        onFiltersChange={handleFilterChange}
        showFilters={showFilters}
        onClose={() => setShowFilters(false)}
      />

      {isLoading && !results.length ? (
        <div className="explore-listings__loading">
          <LoadingSpinner />
        </div>
      ) : (
        <SearchResults
          results={results}
          secondaryResults={secondaryResults}
          hasDetailFilters={hasDetailFilters}
          onContactUser={handleContactUser}
          onViewOnMap={handleViewOnMap}
          onLoadMore={handleLoadMore}
          isLoadingMore={isLoadingMore}
          hasMore={pagination.has_more || secondaryPagination.has_more}
          totalResults={pagination.total}
          secondaryTotalResults={secondaryPagination.total}
          noFiltersSet={noFiltersSet}
          onScroll={handleScroll}
          showHeaders={showHeaders}
          setShowRemoveListingDialog={setShowRemoveListingDialog}
          showRemoveListingDialog={showRemoveListingDialog}
          onRemoveResult={handleRemoveResult}
          fetchLandingPageFeed={fetchFeed}
          handleRefreshFeedClick={handleRefreshFeedClick}
          performSearch={performSearch}
        />
      )}
    </div>
  );
};

export default ExploreListings; 